import React from 'react';
import { Box, Divider, Typography } from '@mui/material';
import { EventNoteOutlined } from '@mui/icons-material';
import { SCHEDULE_COLOR } from '../constants';
import { InspectorSection, StyleRow, PxInput, ColorInput, PanelHeader, VisibilityControl } from '../ui/primitives';

// ─── Breakpoint helpers — same pattern as SpeakersInspector ──────────────────
function getSchedProp(el, bp, key) {
  if (!bp || bp === 'desktop') return el[key];
  return el.responsive?.[bp]?.[key] ?? el[key];
}
function setSchedProp(el, bp, key, val) {
  if (!bp || bp === 'desktop') return { ...el, [key]: val };
  return {
    ...el,
    responsive: {
      ...(el.responsive || {}),
      [bp]: { ...(el.responsive?.[bp] || {}), [key]: val },
    },
  };
}

// ─── Toggle pill ──────────────────────────────────────────────────────────────
function Toggle({ value, onChange }) {
  return (
    <Box onClick={() => onChange(!value)}
      sx={{
        width: 36, height: 20, borderRadius: 10, cursor: 'pointer', position: 'relative', flexShrink: 0,
        background: value ? SCHEDULE_COLOR : '#d1d9e0',
        transition: 'background 0.2s',
      }}>
      <Box sx={{
        position: 'absolute', top: 2, width: 16, height: 16, borderRadius: '50%',
        background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        transition: 'left 0.2s', left: value ? 18 : 2,
      }} />
    </Box>
  );
}

// Label fills all available space, toggle pinned far right
function ToggleRow({ label, value, onChange }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75, gap: 1 }}>
      <Typography sx={{ fontSize: '0.75rem', color: '#5a6474', fontWeight: 500, flex: 1 }}>{label}</Typography>
      <Toggle value={value} onChange={onChange} />
    </Box>
  );
}

// ─── ScheduleInspector ───────────────────────────────────────────────────────
export function ScheduleInspector({ el, onUpdate, onClose, breakpoint = 'desktop' }) {
  const bp = breakpoint;

  // Global — always write to el root (same for all breakpoints)
  const updateGlobal = (key, val) => onUpdate({ ...el, [key]: val });

  // Breakpoint-aware — writes to el.responsive[bp] on non-desktop,
  // exactly matching TextInspector's updateStyle pattern
  const get    = (key)      => getSchedProp(el, bp, key);
  const update = (key, val) => onUpdate(setSchedProp(el, bp, key, val));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PanelHeader
        icon={<EventNoteOutlined sx={{ fontSize: 14 }} />}
        label="Schedule" color={SCHEDULE_COLOR} onClose={onClose} breakpoint={bp}
      />
      <Box sx={{ p: 1.25, display: 'flex', flexDirection: 'column', gap: 1.5, overflowY: 'auto', overflowX: 'hidden' }}>

        {/* ── Colors — global ── */}
        <InspectorSection label="Colors">
          <StyleRow label="Primary">
            <ColorInput value={el.primaryColor || '#1075BA'} onChange={v => updateGlobal('primaryColor', v)} />
          </StyleRow>
          <StyleRow label="Secondary">
            <ColorInput value={el.secondaryColor || '#6b7280'} onChange={v => updateGlobal('secondaryColor', v)} />
          </StyleRow>
          <StyleRow label="Divider">
            <ColorInput value={el.dividerColor || '#f0f4f8'} onChange={v => updateGlobal('dividerColor', v)} />
          </StyleRow>
        </InspectorSection>

        <Divider sx={{ borderColor: '#f0f2f5' }} />

        {/* ── Typography — bp-aware ── */}
        <InspectorSection label="Typography">
          <StyleRow label="Time size">
            <PxInput value={get('timeFontSize')} onChange={v => update('timeFontSize', v)} placeholder="22" />
          </StyleRow>
          <StyleRow label="Title size">
            <PxInput value={get('titleFontSize')} onChange={v => update('titleFontSize', v)} placeholder="25" />
          </StyleRow>
          <StyleRow label="Desc size">
            <PxInput value={get('descFontSize')} onChange={v => update('descFontSize', v)} placeholder="13" />
          </StyleRow>
        </InspectorSection>

        <Divider sx={{ borderColor: '#f0f2f5' }} />

        {/* ── Visibility toggles — bp-aware, toggle pinned right ── */}
        <InspectorSection label="Visibility">
          <ToggleRow label="Speakers"    value={get('showSpeakers')    !== false} onChange={v => update('showSpeakers', v)} />
          <ToggleRow label="Banner"      value={get('showBanner')      !== false} onChange={v => update('showBanner', v)} />
          <ToggleRow label="Description" value={get('showDescription') !== false} onChange={v => update('showDescription', v)} />
          <ToggleRow label="Location"    value={get('showLocation')    !== false} onChange={v => update('showLocation', v)} />
        </InspectorSection>

        <Divider sx={{ borderColor: '#f0f2f5' }} />

        {/* ── Responsive — identical to TextInspector ── */}
        <InspectorSection label="Responsive">
          <VisibilityControl visibleOn={el.visibleOn} onChange={v => onUpdate({ ...el, visibleOn: v })} />
        </InspectorSection>

      </Box>
    </Box>
  );
}
